import React, {
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {
  arrow,
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole
} from '@floating-ui/react'

import {useDeviceLayout} from '../../../context/deviceLayout'
import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Button} from '../Button'
import {Box} from '../helpers/Box'
import {Icon} from '../Icon'
import {Text} from '../Text'

export enum TooltipPlacement {
  Auto = 'auto',
  AutoStart = 'auto-start',
  AutoEnd = 'auto-end',
  Top = 'top',
  TopStart = 'top-start',
  TopEnd = 'top-end',
  Bottom = 'bottom',
  BottomStart = 'bottom-start',
  BottomEnd = 'bottom-end',
  Right = 'right',
  RightStart = 'right-start',
  RightEnd = 'right-end',
  Left = 'left',
  LeftStart = 'left-start',
  LeftEnd = 'left-end'
}

const TOOLTIP_MAX_WIDTH = 448
const ARROW_OFFSET = 4

type IconAlignment = 'flex-start' | 'center'

interface Props {
  /** Open or closed state of the Tooltip */
  isOpen?: boolean
  /** Tooltip title */
  title?: string
  /** Tooltip content */
  content: ReactNode
  /** Tooltip placement relative to trigger component */
  placement?: TooltipPlacement
  /** Trigger component */
  children?: ReactNode
  /** Handler that fires when the tooltip is shown */
  onRender?: () => void
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Whether it should render inline */
  isInline?: boolean
  /** Anchor element to show Tooltip near it */
  anchor?: Element | null
  /** Passing this callback will show the close button.
   Pressing the button will cause the callback execution */
  onClose?: () => void
  /** An icon placed before the Tooltip content. It can be any type of React Element */
  icon?: ReactElement
  /** Pass through alignIcon to allow vertically center aligned icon for example */
  alignIcon?: IconAlignment
}

const CloseButtonWrapper = styled.div`
  color: ${cssTheme.colors.tooltip.default.text};
  position: absolute;
  right: 0;
  top: ${cssTheme.layout.spacing.s200};
`

const StyledTooltip = styled.div`
  border-radius: ${cssTheme.layout.radius.md};
  box-shadow: ${cssTheme.shadows.floating};
  background-color: ${cssTheme.colors.tooltip.default.background};
  max-width: ${TOOLTIP_MAX_WIDTH}px;
  margin-left: ${cssTheme.layout.spacing.s300};
  margin-right: ${cssTheme.layout.spacing.s300};
  z-index: 1;
  ${linariaMq.desktopXs} {
    margin-left: 0;
    margin-right: 0;
  }
`

const StyledTriggerWrapper = styled.div`
  display: flex;
  &.isInline {
    display: inline-flex;
  }
`

const StyledArrow = styled.div`
  width: 8px;
  height: 8px;
  position: absolute;
  background-color: ${cssTheme.colors.tooltip.default.background};
  transform: rotate(45deg);
  border-radius: ${cssTheme.layout.radius.xs} 0;

  &[data-side='top'] {
    border-radius: ${cssTheme.layout.radius.xs} 0;
  }

  &[data-side='bottom'] {
    border-radius: ${cssTheme.layout.radius.xs} 0;
  }

  &[data-side='left'] {
    border-radius: 0 ${cssTheme.layout.radius.xs};
  }

  &[data-side='right'] {
    border-radius: 0 ${cssTheme.layout.radius.xs};
  }
`

// Convert TooltipPlacement to floating-ui Placement
const convertPlacement = (placement: TooltipPlacement): Placement => {
  const placementMap: Record<TooltipPlacement, Placement> = {
    [TooltipPlacement.Auto]: 'bottom',
    [TooltipPlacement.AutoStart]: 'bottom-start',
    [TooltipPlacement.AutoEnd]: 'bottom-end',
    [TooltipPlacement.Top]: 'top',
    [TooltipPlacement.TopStart]: 'top-start',
    [TooltipPlacement.TopEnd]: 'top-end',
    [TooltipPlacement.Bottom]: 'bottom',
    [TooltipPlacement.BottomStart]: 'bottom-start',
    [TooltipPlacement.BottomEnd]: 'bottom-end',
    [TooltipPlacement.Right]: 'right',
    [TooltipPlacement.RightStart]: 'right-start',
    [TooltipPlacement.RightEnd]: 'right-end',
    [TooltipPlacement.Left]: 'left',
    [TooltipPlacement.LeftStart]: 'left-start',
    [TooltipPlacement.LeftEnd]: 'left-end'
  }
  return placementMap[placement]
}

export const Tooltip = ({
  children,
  title,
  content,
  placement = TooltipPlacement.Bottom,
  className,
  onRender,
  isInline = true,
  isOpen = false,
  anchor = undefined,
  onClose,
  icon,
  alignIcon = 'flex-start'
}: Props) => {
  const [visible, setVisible] = useState(isOpen)
  const {isMobile} = useDeviceLayout()
  const arrowRef = useRef<HTMLDivElement>(null)

  // Memoize computed values to prevent unnecessary re-renders
  const hasCloseButton = useMemo(
    () => Boolean(onClose) || isMobile,
    [onClose, isMobile]
  )
  const floatingPlacement = useMemo(
    () => convertPlacement(placement),
    [placement]
  )

  const {
    refs,
    floatingStyles,
    context,
    middlewareData,
    placement: finalPlacement
  } = useFloating({
    open: visible,
    onOpenChange: anchor ? undefined : setVisible,
    placement: floatingPlacement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip(),
      shift(),
      arrow({
        element: arrowRef,
        padding: 12
      })
    ]
  })

  const arrowStyles = useMemo(() => {
    const arrowSide = finalPlacement.split('-')[0]
    const {x: arrowX, y: arrowY} = middlewareData.arrow || {}

    const styles: React.CSSProperties = {}
    if (arrowX != null) {
      styles.left = arrowX
    }
    if (arrowY != null) {
      styles.top = arrowY
    }

    // Position arrow on the correct side
    switch (arrowSide) {
      case 'top':
        styles.bottom = -ARROW_OFFSET
        break
      case 'bottom':
        styles.top = -ARROW_OFFSET
        break
      case 'left':
        styles.right = -ARROW_OFFSET
        break
      case 'right':
        styles.left = -ARROW_OFFSET
        break
    }

    return styles
  }, [finalPlacement, middlewareData.arrow])

  const hover = useHover(context, {
    enabled: !isMobile && !anchor
  })
  const focus = useFocus(context, {
    enabled: !anchor
  })
  const click = useClick(context, {
    enabled: !anchor
  })
  const dismiss = useDismiss(context)
  const role = useRole(context, {role: 'tooltip'})

  const {getReferenceProps, getFloatingProps} = useInteractions([
    hover,
    focus,
    click,
    dismiss,
    role
  ])

  const handleCloseButtonClick = useCallback(
    (e: React.SyntheticEvent) => {
      e.preventDefault()
      onClose?.()
      setVisible(false)
    },
    [onClose]
  )

  useEffect(() => {
    setVisible(isOpen)
  }, [isOpen])

  useEffect(() => {
    if (anchor) {
      refs.setReference(anchor)
    }
  }, [anchor, refs])

  useEffect(() => {
    if (visible && onRender) {
      onRender()
    }
  }, [visible, onRender])

  const renderTooltipContent = useCallback(
    () => (
      <>
        {icon && (
          <Box
            display="flex"
            color="tooltip.default.icon"
            height="s600"
            alignItems="center"
          >
            {icon}
          </Box>
        )}
        <div>
          {title && (
            <Text variant="labelM" colorPath="tooltip.default.text">
              {title}
            </Text>
          )}
          <Text variant="bodyS" colorPath="tooltip.default.text">
            {content}
          </Text>
        </div>
        {hasCloseButton && (
          <CloseButtonWrapper>
            <Button
              hasTouchArea
              variant="transparent"
              size="md"
              iconStart={
                <Icon name="Close" size="md" colorPath="tooltip.default.icon" />
              }
              onClick={handleCloseButtonClick}
              aria-label="Close tooltip"
            />
          </CloseButtonWrapper>
        )}
      </>
    ),
    [icon, title, content, hasCloseButton, handleCloseButtonClick]
  )

  const arrowSide = finalPlacement.split('-')[0]

  if (children) {
    return (
      <>
        <StyledTriggerWrapper
          ref={anchor ? undefined : refs.setReference}
          className={cx(isInline && 'isInline')}
          {...(!anchor ? getReferenceProps() : {})}
        >
          {children}
        </StyledTriggerWrapper>
        {visible && (
          <StyledTooltip
            ref={refs.setFloating}
            style={floatingStyles}
            className={className}
            {...getFloatingProps()}
          >
            <Box
              display="flex"
              alignItems={alignIcon}
              paddingTop="s500"
              paddingBottom="s500"
              paddingRight={hasCloseButton ? 's800' : 's600'}
              paddingLeft={icon ? 's400' : 's600'}
              gap={icon ? 's400' : 's000'}
            >
              {renderTooltipContent()}
            </Box>

            <StyledArrow
              ref={arrowRef}
              data-side={arrowSide}
              style={arrowStyles}
            />
          </StyledTooltip>
        )}
      </>
    )
  }

  // If we don't pass the children, we can render the tooltip near the anchor element
  return visible ? (
    <StyledTooltip
      ref={refs.setFloating}
      style={floatingStyles}
      className={className}
      {...getFloatingProps()}
    >
      <Box
        display="flex"
        alignItems={alignIcon}
        gap={icon ? 's400' : 's000'}
        paddingTop="s500"
        paddingBottom="s500"
        paddingRight={hasCloseButton ? 's800' : 's600'}
        paddingLeft="s600"
        className={cx(icon && '--has-icon')}
      >
        {renderTooltipContent()}
      </Box>

      <StyledArrow ref={arrowRef} data-side={arrowSide} style={arrowStyles} />
    </StyledTooltip>
  ) : null
}
